import { SelectMultiple } from 'grommet';
import React, { ComponentProps, ForwardedRef, forwardRef, ReactElement, ReactNode, useMemo, useState } from 'react';

import { SelectOption } from '@/components/form-controls/selects/select-option';
import { Lookup } from '@/types/lookup';

import { MultiSelectList } from './multi-select-list';

// these represent props managed by the component itself, so we don't expose them
type RestrictedGrommetSelectProps = Omit<ComponentProps<typeof SelectMultiple>,
  | 'replace'
  | 'options'
  | 'value'
  | 'onChange'
  | 'labelKey'
  | 'valueKey'
  | 'onSearch'
  | 'ref'
>;

type InMemoryMultiSelectListProps<TLookupId> = {
  /**
   * The list of options to choose from, required.
   */
  options: Lookup<TLookupId>[];

  /**
   * The options that are currently selected, required.
   */
  value: Lookup<TLookupId>[];

  /**
   * Event handler called whenever a value is changed:
   * - the user clears everything
   * - the user selects or deselects an option
   * @param value the selected options after this action
   */
  onChange: (value: Lookup<TLookupId>[]) => void;

  /**
   * Visible label for the form field, optional but recommended.
   */
  label?: string;

  /**
   * Help text to display in a tooltip next to the label, optional.
   */
  help?: ReactNode;

  /**
   * Error message to display when the field is invalid, optional.
   */
  error?: ReactNode;
} & RestrictedGrommetSelectProps;

const Inner = <TLookupId = number>(
  {
    value,
    onChange,
    options,
    ...rest
  }: InMemoryMultiSelectListProps<TLookupId>,
  ref: ForwardedRef<HTMLInputElement>
) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = useMemo(() => {
    if (!options) {
      return [];
    }
    if (!searchTerm) {
      return options;
    }
    return options.filter(
      (lookup) => lookup.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  return (
    <MultiSelectList
      value={value}
      onChange={({ value }: { value: Lookup<TLookupId>[] }) => onChange(value)}
      labelKey="label"
      valueKey="id"
      options={filteredOptions}
      onSearch={setSearchTerm}
      onClose={() => setSearchTerm('')}
      {...rest}
      ref={ref}
    >
      {(option: Lookup<TLookupId>, _index: number, _options: unknown, state: { selected: boolean }) => (
        <SelectOption option={option} isMulti selected={state.selected}/>
      )}
    </MultiSelectList>
  );
};

/**
 * A wrapper around Grommet's <SelectMultiple> component that handles lazy-loading options for you. It works something
 * like this:
 * - if the user scrolls to the bottom of the page, it fetches the next page of options
 * - if the user searches for data, it fetches the first page of options that match that search criteria
 *
 * It doesn't do any caching outside what the user LAST searched.
 */
export const InMemoryMultiSelectList = forwardRef<HTMLInputElement, InMemoryMultiSelectListProps<number>>(Inner) as <TLookupId = number> (props: InMemoryMultiSelectListProps<TLookupId> & { ref?: ForwardedRef<HTMLInputElement> }) => ReactElement;
